import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { api, type ApiClient } from "@/api/client";
import { useSession } from "@/auth/session";
import { readSetting, writeSetting } from "@/utils/storage";

/**
 * The branch the person is working in (based on apps/mobile/src/auth/branch.tsx).
 *
 * Each branch is run like a shop of its own (its own medicines, stock,
 * customers), so the whole app shows one branch at a time. Remembered in the
 * browser per person (`fb.branch.<userId>`), so it opens where they left off,
 * and told to the server with every request (`X-Branch-Id`).
 */

export interface Branch {
  id: string;
  name: string;
  address: string | null;
  phone?: string | null;
  isActive?: boolean;
}

interface BranchValue {
  /** Null when nobody is signed in, or for somebody with no branch at all. */
  branchId: string | null;
  branch: Branch | null;
  /** Every branch this person works in, oldest first. */
  branches: Branch[];
  /** Work in another branch: everything on screen belonged to the old one. */
  setBranch: (id: string) => void;
}

const BranchContext = createContext<BranchValue | null>(null);

const NONE: BranchValue = { branchId: null, branch: null, branches: [], setBranch: () => {} };

export function BranchProvider({
  children,
  onSwitched,
  client = api,
}: {
  children: React.ReactNode;
  /** After a switch: the app goes to Home (App.tsx gives the router's navigate). */
  onSwitched?: () => void;
  client?: ApiClient;
}) {
  const { state } = useSession();
  const me = state.status === "signedIn" ? state.me : null;
  const queryClient = useQueryClient();
  const storageKey = me ? (`fb.branch.${me.userId}` as const) : null;

  // What was chosen in this page, else what was saved last time.
  const [chosen, setChosen] = useState<{ userId: string; id: string } | null>(null);

  const list = useQuery({
    queryKey: ["branches", me?.userId],
    queryFn: () => client.get<{ branches: Branch[] }>("/branches"),
    enabled: me !== null,
    staleTime: 5 * 60_000,
  });
  const branches = useMemo(() => list.data?.branches ?? [], [list.data]);

  const wanted = me ? (chosen?.userId === me.userId ? chosen.id : readSetting(`fb.branch.${me.userId}`)) : null;
  // One this person may use, or their first. `me` knows before the list arrives.
  const allowed = branches.length > 0 ? branches.map((b) => b.id) : (me?.branchIds ?? []);
  const branchId = me ? (wanted && allowed.includes(wanted) ? wanted : (allowed[0] ?? null)) : null;

  /**
   * Set while drawing, not in an effect, on purpose: the pages below start
   * their requests in this same pass, and each one must already say which
   * branch it is for.
   */
  client.setBranch(branchId);

  const setBranch = useCallback(
    (id: string) => {
      if (!me || !storageKey) return;
      writeSetting(storageKey, id);
      setChosen({ userId: me.userId, id });
      client.setBranch(id);
      // Every list on screen belongs to the old branch.
      queryClient.clear();
      onSwitched?.();
    },
    [me, storageKey, client, queryClient, onSwitched],
  );

  const value = useMemo<BranchValue>(
    () => (me ? { branchId, branch: branches.find((b) => b.id === branchId) ?? null, branches, setBranch } : NONE),
    [me, branchId, branches, setBranch],
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch(): BranchValue {
  const value = useContext(BranchContext);
  if (!value) throw new Error("useBranch must be used inside a BranchProvider");
  return value;
}
